import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

export interface BackgroundTask {
  id: string;
  label: string;
  current: number;
  total: number;
  status: 'running' | 'done' | 'error';
  errors: number;
  startedAt: number;
}

interface BackgroundTaskContextType {
  tasks: BackgroundTask[];
  startBulkGeneration: (params: {
    chapterId: number;
    chapterTitle: string;
    questions: { id: string; prompt: string }[];
  }) => void;
  dismissTask: (id: string) => void;
}

const BackgroundTaskContext = createContext<BackgroundTaskContextType>({
  tasks: [],
  startBulkGeneration: () => {},
  dismissTask: () => {},
});

export const useBackgroundTasks = () => useContext(BackgroundTaskContext);

export const BackgroundTaskProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tasks, setTasks] = useState<BackgroundTask[]>([]);
  const abortRef = useRef<Record<string, boolean>>({});

  const updateTask = useCallback((id: string, updates: Partial<BackgroundTask>) => {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
  }, []);

  const startBulkGeneration = useCallback(async (params: {
    chapterId: number;
    chapterTitle: string;
    questions: { id: string; prompt: string }[];
  }) => {
    const taskId = `bulk-${params.chapterId}-${Date.now()}`;
    const task: BackgroundTask = {
      id: taskId,
      label: `Kap. ${params.chapterId} – ${params.chapterTitle}`,
      current: 0,
      total: params.questions.length,
      status: 'running',
      errors: 0,
      startedAt: Date.now(),
    };

    setTasks(prev => [...prev, task]);
    abortRef.current[taskId] = false;

    let errors = 0;
    const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:3000' : 'https://app.fahryo.de';
    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;

    for (let i = 0; i < params.questions.length; i++) {
      if (abortRef.current[taskId]) break;

      const q = params.questions[i];
      updateTask(taskId, { current: i + 1 });

      try {
        const response = await fetch(`${apiBase}/api/ai/generate-image`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ prompt: q.prompt, questionId: q.id, chapterId: params.chapterId }),
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || `Server error ${response.status}`);
        }

        // Server saves to Storage + DB automatically
        const data = await response.json();
      } catch (e) {
        console.error(`Error generating image for question ${q.id}:`, e);
        errors++;
      }
    }

    updateTask(taskId, {
      status: abortRef.current[taskId] ? 'error' : 'done',
      current: params.questions.length,
      errors,
    });
    delete abortRef.current[taskId];
  }, [updateTask]);

  const dismissTask = useCallback((id: string) => {
    abortRef.current[id] = true;
    setTasks(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <BackgroundTaskContext.Provider value={{ tasks, startBulkGeneration, dismissTask }}>
      {children}
    </BackgroundTaskContext.Provider>
  );
};
